<template>
	<div
		class="flow-wrapper"
		:data-type="type"
		:style="style"
		:data-border="border"
		:data-background="background"
		:data-overflow="overflow"
	>
		<slot />
	</div>
</template>
<script lang="ts">
import { defineComponent, PropType } from "vue";
import { CSSProperties } from "vue/types/jsx";
import colorNames from "../utils/ColorNames";

const BACKGROUND = [
	"pop-over",
	"default",
	"element-light",
	"element",
	"element-dark",
	"font-dark",
	"font",
	"font-light",
	"icon-dark",
	"icon",
	"icon-light",
	"border-dark",
	"border",
	"border-light",
	"primary-dark",
	"primary",
	"primary-light",
	"secondary-dark",
	"secondary",
	"secondary-light",
	"error-dark",
	"error",
	"error-light",
	"warning-dark",
	"warning",
	"warning-light",
	"success-dark",
	"success",
	"success-light",
	"dashboard-background",
	"tooltip",
	"placeholder",
	...colorNames
] as const;

export type WrapperBackground = typeof BACKGROUND[number];

export default defineComponent({
	name: "Wrapper",
	props: {
		/**
		 * 'default' <br/>
		 *
		 * We don't have more types yet and will remove type if haven't found any usecase
		 */
		type: {
			type: String as PropType<"default">,
			default: "default",
			validator(value: "default") {
				return ["default"].includes(value);
			}
		},

		/**
		 * Do we want ot display border? <br/>
		 * true | false
		 */
		border: {
			type: Boolean,
			default: false
		},

		/**
		 * Border radius of wrapper
		 */
		borderRadius: {
			type: String as PropType<CSSProperties["borderRadius"]>,
			default: "0px"
		},

		/**
		 * 	Set background to wrapper <br/>
		 * 'gray-100' | 'gray-200' | 'gray-300' | 'gray-400' | 'gray-500' | 'gray-600' | 'gray-700' | 'gray-800' | 'gray-900' | 'primary-100' | 'primary-200' | 'primary-300' | 'primary-400' | 'primary-500' | 'secondary-100' | 'secondary-200' | 'secondary-300' | 'secondary-400' | 'secondary-500' | 'warning-100' | 'warning-200' | 'warning-300' | 'warning-400' | 'warning-500' | 'success-100' | 'success-200' | 'success-300' | 'success-400' | 'success-500' | 'danger-100' | 'danger-200' | 'danger-300' | 'danger-400' | 'danger-500' | 'color1-100' | 'color1-200' | 'color1-300' | 'color2-100' | 'color2-200' | 'color2-300' | 'color3-100' | 'color3-200' | 'color3-300' | 'color4-100' | 'color4-200' | 'color4-300' | 'color5-100' | 'color5-200' | 'color5-300' | 'color6-100' | 'color6-200' | 'color6-300' | 'pop-over' | 'default' | 'element-light' | 'element' | 'element-dark' | 'font-dark' | 'font' | 'font-light' | 'icon-dark' | 'icon' | 'icon-light' | 'border-dark' | 'border' | 'border-light' | 'primary-dark' | 'primary' | 'primary-light' | 'secondary-dark' | 'secondary' | 'secondary-light' | 'error-dark' | 'error' | 'error-light' | 'warning-dark' | 'warning' | 'warning-light' | 'success-dark' | 'success' | 'success-light' | 'dashboard-background' | 'tooltip' | 'placeholder'
		 */
		background: {
			type: String as PropType<WrapperBackground>,
			default: "default",
			validator(value: WrapperBackground) {
				return BACKGROUND.includes(value);
			}
		},

		/**
		 * Set height of wrapper
		 */
		height: {
			type: String as PropType<CSSProperties["height"]>,
			default: "100%"
		},

		/**
		 * Set max-height of wrapper
		 */
		maxHeight: {
			type: String as PropType<CSSProperties["maxHeight"]>,
			default: "initial"
		},

		/**
		 * Set width of wrapper
		 */
		width: {
			type: String as PropType<CSSProperties["width"]>,
			default: "100%"
		},

		/**
		 * How content overflow should work? <br/>
		 * 'hidden' | 'auto' | 'scroll' | 'visible'
		 */
		overflow: {
			type: String,
			default: "hidden"
		}
	},
	computed: {
		style() {
			const styleObj: CSSProperties = {
				borderRadius: this.borderRadius,
				height: this.height,
				maxHeight: this.maxHeight,
				width: this.width
			};
			return styleObj;
		}
	}
});
</script>
<style lang="scss">
@import "../styles/theme-variables/color-names";
$overflows: hidden, auto, scroll, visible;
$backgrounds: map-merge(
	$color-names,
	(
		"pop-over": var(--slideout-background),
		"default": transparent,
		"element-light": var(--dashboard-element-light),
		"element": var(--dashboard-element),
		"element-dark": var(--dashboard-element-dark),
		"font-dark": var(--dashboard-font-dark),
		"font": var(--dashboard-font),
		"font-light": var(--dashboard-font-light),
		"icon-dark": var(--dashboard-icon-dark),
		"icon": var(--dashboard-icon),
		"icon-light": var(--dashboard-icon-light),
		"border-dark": var(--dashboard-border-dark),
		"border": var(--dashboard-border),
		"border-light": var(--dashboard-border-light),
		"primary-dark": var(--dashboard-primary-dark),
		"primary": var(--dashboard-primary),
		"primary-light": var(--dashboard-primary-light),
		"secondary-dark": var(--dashboard-secondary-dark),
		"secondary": var(--dashboard-secondary),
		"secondary-light": var(--dashboard-secondary-light),
		"error-dark": var(--dashboard-danger-dark),
		"error": var(--dashboard-danger),
		"error-light": var(--dashboard-danger-light),
		"warning-dark": var(--dashboard-warning-dark),
		"warning": var(--dashboard-warning),
		"warning-light": var(--dashboard-warning-light),
		"success-dark": var(--dashboard-success-dark),
		"success": var(--dashboard-success),
		"success-light": var(--dashboard-success-light),
		"dashboard-background": var(--dashboard-background),
		"tooltip": rgba(0, 0, 0, 0.9),
		"placeholder": linear-gradient(90deg, rgba(0, 0, 0, 0.2) 0.44%, rgba(0, 0, 0, 0.098) 99.52%)
	)
);
div.flow-wrapper {
	display: flex;
	flex-direction: column;
	max-width: 100vw;
	max-height: 100vh;
	overflow: hidden;
	// justify-content: center;

	&[data-border] {
		border: 1px solid var(--dashboard-border);
	}
	@each $name, $background in $backgrounds {
		&[data-background="#{$name}"] {
			background: $background;
		}
	}

	@each $overflow in $overflows {
		&[data-overflow="#{$overflow}"] {
			overflow: $overflow;
		}
	}
}
</style>
